import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Check, ShieldCheck, FileText, BarChart3, Target, Layers, AlertTriangle, GitBranch, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { trackViewContent, trackClickButton } from "../lib/tiktok";

const PRICE = "$79";

const FONT = "'Inter','SF Pro Display',system-ui,-apple-system,sans-serif";
const SERIF = "'Fraunces','Playfair Display',Georgia,serif";

const ensureFonts = () => {
  if (document.getElementById("ipo-font")) return;
  const l = document.createElement("link");
  l.id = "ipo-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Fraunces:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
};

// First-party funnel analytics — beacon survives the redirect to Stripe.
const vid = () => {
  try {
    let v = localStorage.getItem("ipo_vid");
    if (!v) {
      v = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now()}`;
      localStorage.setItem("ipo_vid", v);
    }
    return v;
  } catch (_) { return "anon"; }
};
const track = (event, props = {}) => {
  try {
    const payload = JSON.stringify({ event, props, visitor_id: vid(), page: "ipoaccess" });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ipo/track`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    else api.post("/ipo/track", JSON.parse(payload));
  } catch (_) { /* analytics must never break the funnel */ }
};

// Premium hero artwork — a stylized intelligence dossier with valuation chart.
function DossierArtwork() {
  return (
    <div className="relative w-full max-w-[460px] mx-auto" data-testid="ipo-hero-artwork">
      <div className="absolute -inset-8 bg-[#3F8EFC]/[0.10] blur-3xl rounded-full pointer-events-none" aria-hidden />
      {/* Faint grid backdrop */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(63,142,252,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(63,142,252,0.07) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
        aria-hidden
      />
      <div className="relative bg-[#0E1218] border border-[#3F8EFC]/25 rounded-md p-6 shadow-[0_30px_80px_-25px_rgba(63,142,252,0.45)] backdrop-blur">
        {/* Header bar */}
        <div className="flex items-center justify-between pb-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-sm bg-gradient-to-br from-[#3F8EFC] to-[#1E5BD8] flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" strokeWidth={2} />
            </span>
            <div>
              <div className="text-[10px] tracking-[0.28em] text-[#3F8EFC] font-bold">DOSSIER № 001</div>
              <div className="text-[13px] text-white font-semibold">IPO Access — June 2026</div>
            </div>
          </div>
          <span className="text-[10px] text-[#7A8597] tracking-[0.2em] flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3F8EFC] animate-pulse" /> CONFIDENTIAL
          </span>
        </div>

        {/* Valuation pretend-chart */}
        <div className="mt-5">
          <div className="flex items-end justify-between mb-1">
            <span className="text-[10px] text-[#7A8597] tracking-[0.2em] font-bold">VALUATION TRAJECTORY</span>
            <span className="text-[10px] text-[#3F8EFC] font-bold">+184%</span>
          </div>
          <svg viewBox="0 0 300 80" className="w-full h-20" preserveAspectRatio="none" aria-hidden>
            <defs>
              <linearGradient id="ipoLine" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#3F8EFC" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#3F8EFC" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M0 60 L30 55 L60 48 L90 52 L120 40 L150 35 L180 28 L210 30 L240 18 L270 22 L300 10 L300 80 L0 80 Z" fill="url(#ipoLine)" />
            <path d="M0 60 L30 55 L60 48 L90 52 L120 40 L150 35 L180 28 L210 30 L240 18 L270 22 L300 10" fill="none" stroke="#3F8EFC" strokeWidth="1.8" />
          </svg>
        </div>

        {/* Stat row */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[
            { l: "PRE-MONEY", v: "$248B" },
            { l: "INSIDER SELL", v: "12.4%" },
            { l: "DEMAND/SUPPLY", v: "3.8×" },
          ].map((s) => (
            <div key={s.l} className="border-l border-[#3F8EFC]/40 pl-2.5 py-1">
              <div className="text-[9px] text-[#7A8597] tracking-[0.22em] font-bold">{s.l}</div>
              <div className="text-[15px] font-bold text-white tabular-nums">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Scoreboard */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="bg-white/[0.025] border border-white/[0.07] rounded-sm p-3">
            <div className="text-[9px] tracking-[0.22em] text-[#7A8597] font-bold mb-2">OPPORTUNITY SCORE</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-bold text-[#3F8EFC] tabular-nums">62</span>
              <span className="text-[11px] text-[#7A8597]">/100</span>
            </div>
            <div className="mt-2 h-1 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#3F8EFC] to-[#7EB2FF]" style={{ width: "62%" }} />
            </div>
          </div>
          <div className="bg-white/[0.025] border border-white/[0.07] rounded-sm p-3">
            <div className="text-[9px] tracking-[0.22em] text-[#7A8597] font-bold mb-2">DECISION</div>
            <div className="text-[15px] font-bold text-white">Watch</div>
            <div className="text-[10.5px] text-[#7A8597] mt-1 leading-relaxed">Wait for post-IPO de-risk</div>
          </div>
        </div>
      </div>

      {/* Tag chip */}
      <div className="absolute -bottom-3 left-6 bg-[#0E1218] border border-[#3F8EFC]/40 rounded-sm px-3 py-1.5 text-[10px] tracking-[0.22em] text-[#3F8EFC] font-bold shadow-lg">
        ACCESS · VALUATION · OPPORTUNITY
      </div>
    </div>
  );
}

export default function LandingIPO() {
  const navigate = useNavigate();

  useEffect(() => {
    ensureFonts();
    document.title = "IPO Access Dossier™ — Before You Chase The Next Big IPO";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Institutional-style intelligence for retail investors evaluating high-demand IPOs and pre-IPO opportunities. Access, valuation, and decision framework — one dossier, one decision.");
    let icon = document.querySelector("link[rel='icon']");
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = "/ipo-access-dossier-logo.jpg";
    trackViewContent("ipo_access_dossier");
    track("page_view");
  }, []);

  useEffect(() => {
    const start = Date.now();
    let sent = false;
    const send = () => {
      if (sent) return;
      const seconds = Math.round((Date.now() - start) / 1000);
      if (seconds < 2) return;
      sent = true;
      track("time_on_site", { seconds });
    };
    const onHide = () => { if (document.visibilityState === "hidden") send(); };
    window.addEventListener("pagehide", send);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", send);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, []);

  const goToCheckout = (target = "primary") => {
    try { trackClickButton(`ipo-${target}`); } catch (_) { /* analytics must never break the funnel */ }
    track("cta_click", { target });
    navigate("/ipoaccess/checkout");
  };

  return (
    <div
      dir="ltr"
      lang="en"
      className="min-h-screen bg-[#0A0D12] text-[#E7EAF0] selection:bg-[#3F8EFC]/40"
      style={{ fontFamily: FONT, textAlign: "left" }}
      data-testid="ipo-landing-root"
    >
      {/* NAV */}
      <nav className="px-5 pt-5 max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="ipo-logo">
          <img src="/ipo-access-dossier-logo.jpg" alt="IPO Access Dossier" className="h-9 w-9 rounded-sm object-cover" />
          <div className="leading-tight">
            <div className="font-bold text-[13px] tracking-tight">IPO Access Dossier<span className="text-[#3F8EFC]">™</span></div>
            <div className="text-[9.5px] text-[#7A8597] tracking-[0.18em]">EARLY ACCESS · INSIGHT · ADVANTAGE</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-[#7A8597]">
          <ShieldCheck className="w-3.5 h-3.5 text-[#3F8EFC]" /> Independent research · No advisory relationship
        </div>
      </nav>

      {/* HERO */}
      <header className="px-5 pt-12 sm:pt-20 pb-14 sm:pb-20 relative overflow-hidden" data-testid="ipo-hero">
        <div className="absolute -top-40 -right-32 w-[460px] h-[460px] rounded-full bg-[#3F8EFC]/[0.10] blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute top-40 -left-40 w-[360px] h-[360px] rounded-full bg-[#1E5BD8]/[0.10] blur-3xl pointer-events-none" aria-hidden />

        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-sm bg-[#3F8EFC]/[0.08] border border-[#3F8EFC]/30 text-[10.5px] tracking-[0.22em] text-[#3F8EFC] font-bold mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3F8EFC] animate-pulse" /> PRIVATE INTELLIGENCE BRIEF
            </div>
            <h1
              className="font-bold text-[34px] sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight text-white"
              style={{ fontFamily: SERIF, fontWeight: 600 }}
              data-testid="ipo-hero-title"
            >
              Before You Chase<br />
              The Next Big IPO,<br />
              Know Whether You&apos;re <span className="text-[#3F8EFC]">Early</span>...<br />
              <span className="text-[#7A8597] italic">Or Exit Liquidity.</span>
            </h1>
            <p className="mt-6 text-[15px] sm:text-[16px] text-[#A5AFC0] leading-[1.75] max-w-[560px]">
              The IPO Access Dossier™ — also known as the <span className="text-white font-semibold">Wealth Access Dossier</span> — reveals how retail investors can access high-demand IPOs and pre-IPO opportunities, what they&apos;re really worth, and where the crowd may be getting it wrong.
            </p>

            {/* CTA — email is collected on the next page */}
            <div className="mt-8 max-w-[520px]" data-testid="ipo-hero-cta-block">
              <button
                onClick={() => goToCheckout("hero")}
                data-testid="ipo-hero-cta"
                className="w-full sm:w-auto bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[15px] tracking-wide px-8 py-4 rounded-sm transition-all flex items-center justify-center gap-2 min-h-[56px] shadow-[0_10px_36px_-10px_rgba(63,142,252,0.65)]"
              >
                Get The Dossier — {PRICE} <ArrowRight className="w-4 h-4" />
              </button>
              <div className="mt-3 text-[11.5px] text-[#7A8597] flex items-center gap-1.5">
                <Lock className="w-3 h-3 text-[#3F8EFC]" /> Secure Stripe checkout · One-time · Instant digital delivery
              </div>
            </div>

            {/* Secondary trust statement */}
            <div className="mt-9 flex items-center gap-6 text-[12px] text-[#7A8597] tracking-[0.18em] font-semibold uppercase" data-testid="ipo-trust-row">
              <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#3F8EFC]" /> One Company</span>
              <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#3F8EFC]" /> One Deep-Dive</span>
              <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#3F8EFC]" /> One Decision</span>
            </div>
          </div>

          {/* Right artwork */}
          <DossierArtwork />
        </div>
      </header>

      {/* SECTION 2 — Most retail investors */}
      <section className="px-5 py-16 sm:py-24 border-t border-white/[0.06]" data-testid="ipo-comparison">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-bold text-[28px] sm:text-[42px] leading-[1.1] tracking-tight text-white max-w-3xl" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            Most Retail Investors Don&apos;t Miss Opportunities.
            <br />
            <span className="text-[#3F8EFC]">They Buy Them Too Late.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              {
                key: "retail",
                tag: "RETAIL INVESTOR",
                points: ["Sees headlines", "Follows social media", "Buys after excitement"],
                tone: "muted",
              },
              {
                key: "institutional",
                tag: "INSTITUTIONAL INVESTOR",
                points: ["Reviews valuation", "Understands allocation", "Studies downside"],
                tone: "muted",
              },
              {
                key: "dossier",
                tag: "IPO ACCESS DOSSIER™",
                points: ["Access pathways", "Valuation reality check", "Hype analysis", "Opportunity score", "Risk scenarios"],
                tone: "accent",
              },
            ].map((card) => (
              <div
                key={card.key}
                data-testid={`ipo-compare-${card.key}`}
                className={`rounded-sm p-7 transition-shadow ${
                  card.tone === "accent"
                    ? "bg-gradient-to-b from-[#0F1A2D] to-[#0B121E] border border-[#3F8EFC]/45 shadow-[0_0_50px_-18px_rgba(63,142,252,0.5)]"
                    : "bg-[#0E1218] border border-white/[0.08]"
                }`}
              >
                <div className={`text-[10px] tracking-[0.24em] font-bold mb-5 ${card.tone === "accent" ? "text-[#3F8EFC]" : "text-[#7A8597]"}`}>
                  {card.tag}
                </div>
                <ul className="space-y-3 text-[14px] text-[#D6DCE7] leading-relaxed">
                  {card.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-2 ${card.tone === "accent" ? "bg-[#3F8EFC]" : "bg-[#5A6275]"}`} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — Inside every dossier */}
      <section className="px-5 py-16 sm:py-24 bg-[#0C1018] border-y border-white/[0.06]" data-testid="ipo-inside">
        <div className="max-w-6xl mx-auto">
          <div className="text-[11px] text-[#3F8EFC] tracking-[0.28em] font-bold mb-3">DELIVERABLES</div>
          <h2 className="font-bold text-[28px] sm:text-[42px] leading-[1.1] tracking-tight text-white" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            Inside Every IPO Access Dossier<span className="text-[#3F8EFC]">™</span>
          </h2>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: Target, n: "01", t: "Access Map",
                lines: ["Where access is available", "Broker/platform options", "Eligibility requirements", "Allocation mechanics"],
              },
              {
                icon: BarChart3, n: "02", t: "Valuation Reality Check",
                lines: ["Current valuation", "Comparable companies", "Growth expectations", "Fair-value framework"],
              },
              {
                icon: Layers, n: "03", t: "Hype vs Substance Score",
                lines: ["Narrative strength", "Institutional demand", "Retail demand", "Valuation risk"],
              },
              {
                icon: ShieldCheck, n: "04", t: "Opportunity Score",
                lines: ["Access attractiveness", "Risk profile", "Crowd positioning", "Overall assessment"],
              },
              {
                icon: AlertTriangle, n: "05", t: "Scenario Analysis",
                lines: ["Bull case", "Base case", "Bear case"],
              },
              {
                icon: GitBranch, n: "06", t: "Decision Framework",
                lines: ["Chase", "Watch", "Wait", "Avoid"],
              },
            ].map((c, i) => (
              <div
                key={c.n}
                className="bg-[#0E1218] border border-white/[0.08] rounded-sm p-6 hover:border-[#3F8EFC]/35 transition-colors"
                data-testid={`ipo-deliverable-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <span className="w-10 h-10 rounded-sm bg-[#3F8EFC]/[0.12] border border-[#3F8EFC]/30 flex items-center justify-center">
                    <c.icon className="w-4.5 h-4.5 text-[#3F8EFC]" strokeWidth={1.8} />
                  </span>
                  <span className="text-[11px] text-[#5A6275] tracking-[0.2em] font-bold">№ {c.n}</span>
                </div>
                <div className="font-bold text-[16.5px] text-white mb-3 leading-tight">{c.t}</div>
                <ul className="space-y-1.5 text-[13px] text-[#A5AFC0] leading-relaxed">
                  {c.lines.map((l) => (
                    <li key={l} className="flex items-start gap-2.5">
                      <Check className="w-3 h-3 text-[#3F8EFC] shrink-0 mt-1.5" strokeWidth={2.5} />
                      <span>{l}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — What makes this different */}
      <section className="px-5 py-16 sm:py-24" data-testid="ipo-different">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-bold text-[28px] sm:text-[42px] leading-[1.1] tracking-tight text-white text-center" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            What Makes This Different
          </h2>

          <div className="mt-12 border border-white/[0.08] rounded-sm overflow-hidden">
            {[
              { src: "Finance YouTube", v: "Entertainment", dossier: "Structured intelligence" },
              { src: "News Articles", v: "Headlines", dossier: "Structured intelligence" },
              { src: "Social Media", v: "Opinions", dossier: "Signal" },
              { src: "Finance YouTube", v: "Reactive", dossier: "Decision-focused" },
              { src: "Social Media", v: "Noise", dossier: "Signal" },
            ].map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.2fr] gap-0 ${i !== 0 ? "border-t border-white/[0.06]" : ""}`}
                data-testid={`ipo-diff-row-${i}`}
              >
                <div className="px-5 py-4 text-[12px] tracking-[0.2em] text-[#7A8597] font-bold flex items-center bg-white/[0.015]">{row.src}</div>
                <div className="px-5 py-4 text-[14.5px] text-[#A5AFC0]">{row.v}</div>
                <div className="px-5 py-4 text-[14.5px] text-white bg-[#3F8EFC]/[0.05] border-l border-[#3F8EFC]/25 flex items-center gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#3F8EFC]" /> {row.dossier}
                </div>
              </div>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.2fr] gap-0 border-t border-white/[0.06] bg-[#0E1218]">
              <div className="px-5 py-4 text-[11px] tracking-[0.24em] text-[#5A6275] font-bold">SOURCE</div>
              <div className="px-5 py-4 text-[11px] tracking-[0.24em] text-[#5A6275] font-bold">WHAT YOU GET</div>
              <div className="px-5 py-4 text-[11px] tracking-[0.24em] text-[#3F8EFC] font-bold border-l border-[#3F8EFC]/25">IPO ACCESS DOSSIER™</div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Designed for */}
      <section className="px-5 py-16 sm:py-24 bg-[#0C1018] border-y border-white/[0.06]" data-testid="ipo-audience">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-bold text-[28px] sm:text-[40px] leading-[1.1] tracking-tight text-white" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            Designed For Investors Who Think Before They Click <span className="text-[#3F8EFC]">Buy.</span>
          </h2>
          <ul className="mt-10 space-y-4 text-[15px] text-[#D6DCE7] leading-relaxed">
            {[
              "Investors evaluating a major IPO",
              "Investors considering pre-IPO access",
              "Investors comparing valuation vs hype",
              "Investors wanting institutional-style analysis",
              "Investors who refuse to invest based on social media excitement alone",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3" data-testid={`ipo-audience-${t.slice(0, 6).toLowerCase().replace(/\s/g, "-")}`}>
                <Check className="w-5 h-5 text-[#3F8EFC] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 6 — Closing CTA + quote + price */}
      <section className="px-5 py-20 sm:py-28" data-testid="ipo-closing">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-bold text-[28px] sm:text-[44px] leading-[1.08] tracking-tight text-white" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            The Question Isn&apos;t Whether The IPO Is Popular.
            <br />
            <span className="text-[#3F8EFC] italic">The Question Is Whether It&apos;s Worth Chasing.</span>
          </h2>

          <blockquote className="mt-14 max-w-2xl mx-auto" data-testid="ipo-quote">
            <div className="text-[#3F8EFC] text-5xl leading-none mb-4" style={{ fontFamily: SERIF }}>&ldquo;</div>
            <p className="text-[22px] sm:text-[28px] text-white leading-[1.35] tracking-tight" style={{ fontFamily: SERIF, fontWeight: 500 }}>
              The crowd creates opportunity.<br />
              <span className="text-[#7A8597]">The crowd also creates bubbles.</span>
            </p>
          </blockquote>

          {/* Price card */}
          <div className="mt-16 max-w-md mx-auto bg-[#0E1218] border border-[#3F8EFC]/40 rounded-sm p-8 shadow-[0_30px_80px_-25px_rgba(63,142,252,0.55)]" data-testid="ipo-price-card">
            <div className="text-[10.5px] tracking-[0.28em] text-[#3F8EFC] font-bold">IPO ACCESS DOSSIER<span className="text-white">™</span></div>
            <div className="mt-3 flex items-baseline justify-center gap-1">
              <span className="text-6xl font-bold text-white tabular-nums" style={{ fontFamily: SERIF, fontWeight: 600 }}>$79</span>
              <span className="text-[#7A8597] text-[13px]">USD</span>
            </div>
            <div className="text-[12px] text-[#7A8597] mt-1 tracking-wide">One-time purchase</div>

            <ul className="mt-7 text-[13.5px] text-[#D6DCE7] space-y-2.5 text-left">
              {["Instant digital delivery", "No subscription", "No monthly fees", "Independent institutional-style research"].map((t) => (
                <li key={t} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-[#3F8EFC]" strokeWidth={2.5} /> {t}
                </li>
              ))}
            </ul>

            <button
              onClick={() => goToCheckout("price-card")}
              data-testid="ipo-price-cta"
              className="mt-7 w-full bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[15px] tracking-wide px-7 py-4 rounded-sm transition-all flex items-center justify-center gap-2 min-h-[54px] shadow-[0_10px_36px_-10px_rgba(63,142,252,0.65)]"
            >
              Get The Dossier <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-3 text-[11px] text-[#7A8597] flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3 text-[#3F8EFC]" /> Secure Stripe checkout · Email entered on the next step
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-5 py-16 sm:py-20 bg-[#0C1018] border-t border-white/[0.06]" data-testid="ipo-faq">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-bold text-[26px] sm:text-[34px] text-center tracking-tight text-white mb-10" style={{ fontFamily: SERIF, fontWeight: 600 }}>FAQ</h2>
          <div className="space-y-3">
            {[
              { q: "Is this financial advice?", a: "No. The IPO Access Dossier™ is an independent research and educational intelligence product designed to help investors evaluate opportunities. It does not constitute personalised financial, legal, or tax advice." },
              { q: "How often is the dossier updated?", a: "Each dossier is produced for a specific company or opportunity and reflects the most current available information at publication." },
              { q: "Who is this for?", a: "Retail investors seeking a structured framework for evaluating high-profile IPO and pre-IPO opportunities — not entertainment, not headlines, not influencer opinion." },
              { q: "How do I receive the file?", a: "Immediately after payment, your download page opens automatically. The PDF is also linked to the email you provide at checkout." },
              { q: "Is this a subscription?", a: "No. One-time payment. Instant digital delivery. No recurring fees." },
            ].map((f, i) => (
              <details key={i} className="group bg-[#0E1218] border border-white/[0.08] rounded-sm px-5 py-4 open:border-[#3F8EFC]/30 transition-colors" data-testid={`ipo-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-[14.5px] text-white list-none flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <ChevronDown className="w-4 h-4 text-[#3F8EFC] shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-[13.5px] text-[#A5AFC0] leading-[1.75]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-5 py-20 sm:py-24 border-t border-white/[0.06]" data-testid="ipo-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-bold text-[30px] sm:text-[46px] leading-[1.08] tracking-tight text-white" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            Before You Commit Capital,
            <br />
            <span className="text-[#3F8EFC]">Get The Full Picture.</span>
          </h2>
          <p className="mt-5 text-[15.5px] text-[#A5AFC0] leading-[1.75] max-w-lg mx-auto">
            One decision can be expensive.<br />One dossier can prevent it.
          </p>
          <button
            onClick={() => goToCheckout("final")}
            data-testid="ipo-final-cta-btn"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[15px] tracking-wide px-9 py-4 rounded-sm transition-all min-h-[54px] shadow-[0_10px_36px_-10px_rgba(63,142,252,0.65)]"
          >
            Get Instant Access <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-4 text-[11px] text-[#7A8597] flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3 text-[#3F8EFC]" /> {PRICE} one-time · Instant digital delivery · No subscription
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-10 border-t border-white/[0.06] text-center" data-testid="ipo-footer">
        <div className="flex items-center justify-center gap-2.5 mb-3 opacity-80">
          <img src="/ipo-access-dossier-logo.jpg" alt="" className="h-7 w-7 rounded-sm object-cover" />
          <span className="font-bold text-[13px] text-white">IPO Access Dossier<span className="text-[#3F8EFC]">™</span></span>
        </div>
        <p className="text-[11px] text-[#5A6275] max-w-lg mx-auto leading-relaxed">
          IPO Access Dossier™ is an independent research product. Nothing in this product constitutes investment, legal, or tax advice. Investing in IPO and pre-IPO opportunities involves significant risk, including total loss of capital.
        </p>
        <p className="mt-3 text-[10.5px] text-[#5A6275] tracking-wide" data-testid="ipo-footer-disclaimer">
          Educational research only. No financial advice. No guaranteed results.
        </p>
      </footer>

      {/* MOBILE STICKY CTA */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A0D12]/95 backdrop-blur border-t border-white/[0.08] p-3 flex items-center gap-3">
        <div className="text-[11px] text-[#7A8597] leading-tight">
          <div className="font-bold text-white">IPO Access Dossier™</div>
          <div>One-time · {PRICE}</div>
        </div>
        <button
          onClick={() => goToCheckout("sticky")}
          data-testid="ipo-sticky-cta"
          className="ml-auto bg-[#3F8EFC] text-white font-bold text-sm px-5 py-3 rounded-sm flex items-center gap-1.5 min-h-[48px] shadow-[0_8px_24px_-8px_rgba(63,142,252,0.65)]"
        >
          Get The Dossier
        </button>
      </div>
      <div className="sm:hidden h-20" aria-hidden />
    </div>
  );
}
